
import { useEffect, useState } from "react";
import { useRouter } from "@/lib/router-compat";
import { lazy, Suspense } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { usePageTitle } from '@/hooks/usePageTitle';
import { useSuperAdminOrganizations, useSuperAdminUsers, useImpersonate, useDeleteUser, useDeleteOrganization } from "@/hooks/useSuperAdmin";
import { getOrganizationColumns } from "@/components/superadmin/organizations-columns";
import { getUserColumns } from "@/components/superadmin/users-columns";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TableSkeleton } from '@/components/ui/skeletons';
import { Button } from "@/components/ui/button";
import { Building2, Users, MessageSquare, RefreshCw } from "lucide-react";
import { PageHeader } from '@/components/ui/page-header';
import { apiClient } from "@/lib/api/client";
import { toast } from "sonner";
import { FilterBar, type FilterFieldDefinition } from "@/components/ui/filter-bar";
import { EmptyState } from "@/components/ui/empty-state";
import { DeleteConfirmation } from "@/components/ui/delete-confirmation";
import { useFilterBar } from "@/hooks/useFilterBar";
import { useCardSelection } from "@/hooks/useCardSelection";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const DataTable = lazy(
  () => import("@/components/ui/data-table").then(m => ({ default: m.DataTable }))
) as any;

function SuperAdminPage() {
  usePageTitle('Super Admin');
  const { token, isSuperAdmin } = useAuth();
  const router = useRouter();

  useEffect(() => {
    if (token && !isSuperAdmin) {
      router.push("/dashboard");
    }
  }, [token, isSuperAdmin, router]);

  if (!isSuperAdmin) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <p className="text-muted-foreground">Access denied. Super Admin only.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Super Admin Panel"
        description="View all organizations and users across the platform"
        askAi
        hideTitleOnMobile={false}
      />

      <Tabs defaultValue="organizations">
        <TabsList>
          <TabsTrigger value="organizations" className="gap-2">
            <Building2 className="h-4 w-4" />
            Organizations
          </TabsTrigger>
          <TabsTrigger value="users" className="gap-2">
            <Users className="h-4 w-4" />
            Users
          </TabsTrigger>
          <TabsTrigger value="chat" className="gap-2">
            <MessageSquare className="h-4 w-4" />
            Chat
          </TabsTrigger>
        </TabsList>

        <TabsContent value="organizations" className="mt-4">
          <OrganizationsTab />
        </TabsContent>

        <TabsContent value="users" className="mt-4">
          <UsersTab />
        </TabsContent>

        <TabsContent value="chat" className="mt-4">
          <ChatTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ChatTab() {
  const [loading, setLoading] = useState(false);

  const reloadDocs = async () => {
    setLoading(true);
    try {
      await apiClient.post('/api/superadmin/chat/reload-docs');
      toast.success('Docs reloaded — next AI response will use updated content');
    } catch {
      toast.error('Failed to reload docs');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-lg border p-4 space-y-3">
        <div>
          <p className="text-sm font-medium">Reload AI knowledge base</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Forces the chat AI to re-read all docs MDX files from disk. Use this after updating site content without restarting the API container.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={reloadDocs} disabled={loading}>
          <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
          {loading ? 'Reloading…' : 'Reload docs'}
        </Button>
      </div>
    </div>
  );
}

const organizationFilterFields: FilterFieldDefinition[] = [
  { id: "name", label: "Name", type: "text" },
  { id: "description", label: "Description", type: "text" },
];

function OrganizationsTab() {
  const { data: organizations = [], isLoading } = useSuperAdminOrganizations();
  const deleteOrg = useDeleteOrganization();
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const {
    rowSelection,
    setRowSelection,
    selectedIds: selectedOrgIds,
    clear: clearSelection,
  } = useCardSelection();
  const {
    filterBarProps,
    columnFilters,
    handleTableFiltersChange,
    debouncedSearch,
  } = useFilterBar(organizationFilterFields);

  const handleDeleteOrg = (orgId: string) => {
    deleteOrg.mutate(orgId);
  };

  const handleConfirmBulkDelete = () => {
    for (const orgId of selectedOrgIds) {
      deleteOrg.mutate(orgId);
    }
    clearSelection();
    setBulkDeleteOpen(false);
  };

  const columns = getOrganizationColumns(handleDeleteOrg);

  return (
    <div className="flex flex-col gap-3">
      <FilterBar
        {...filterBarProps}
        searchPlaceholder="Search organizations..."
      />
      <div className="flex h-[520px] flex-col">
        <DataTable
          columns={columns}
          data={organizations}
          isLoading={isLoading}
          gridKey="adminOrganizations"
          filterFields={organizationFilterFields}
          summaryLabel="organizations"
          getRowId={(organization: { id: string }) => organization.id}
          enableRowSelection
          rowSelection={rowSelection}
          onRowSelectionChange={setRowSelection}
          bulkActions={
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setBulkDeleteOpen(true)}
            >
              Delete
            </Button>
          }
          columnFilters={columnFilters}
          onColumnFiltersChange={handleTableFiltersChange}
          globalFilter={debouncedSearch}
          emptyStateNode={
            <EmptyState
              icon={Building2}
              title="No organizations found"
              description="Organizations appear here once users create them."
            />
          }
        />
      </div>

      <DeleteConfirmation
        open={bulkDeleteOpen}
        onOpenChange={setBulkDeleteOpen}
        onConfirm={handleConfirmBulkDelete}
        title={`Delete ${selectedOrgIds.length} organization${selectedOrgIds.length === 1 ? "" : "s"}?`}
        description={`Are you sure you want to delete ${selectedOrgIds.length} organization${selectedOrgIds.length === 1 ? "" : "s"}? This action cannot be undone.`}
        entityType="organization"
      />
    </div>
  );
}

const userFilterFields: FilterFieldDefinition[] = [
  { id: "email", label: "Email", type: "text" },
  { id: "firstName", label: "First Name", type: "text" },
  { id: "lastName", label: "Last Name", type: "text" },
];

function UsersTab() {
  const { data: users = [], isLoading } = useSuperAdminUsers();
  const impersonate = useImpersonate();
  const deleteUser = useDeleteUser();
  const router = useRouter();
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const {
    rowSelection,
    setRowSelection,
    selectedIds: selectedUserIds,
    clear: clearSelection,
  } = useCardSelection();
  const {
    filterBarProps,
    columnFilters,
    handleTableFiltersChange,
    debouncedSearch,
  } = useFilterBar(userFilterFields);

  const handleImpersonate = async (userId: string) => {
    const result = await impersonate.mutateAsync(userId);
    localStorage.setItem('token', result.token);
    localStorage.removeItem('currentOrg');
    window.location.href = '/dashboard';
  };

  const handleDeleteUser = (userId: string) => {
    deleteUser.mutate(userId);
  };

  const handleConfirmBulkDelete = () => {
    for (const userId of selectedUserIds) {
      deleteUser.mutate(userId);
    }
    clearSelection();
    setBulkDeleteOpen(false);
  };

  const columns = getUserColumns(handleImpersonate, handleDeleteUser);

  return (
    <div className="flex flex-col gap-3">
      <FilterBar {...filterBarProps} searchPlaceholder="Search users..." />
      <div className="flex h-[520px] flex-col">
        <DataTable
          columns={columns}
          data={users}
          isLoading={isLoading}
          gridKey="adminUsers"
          filterFields={userFilterFields}
          summaryLabel="users"
          getRowId={(user: { id: string }) => user.id}
          enableRowSelection
          rowSelection={rowSelection}
          onRowSelectionChange={setRowSelection}
          bulkActions={
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setBulkDeleteOpen(true)}
            >
              Delete
            </Button>
          }
          columnFilters={columnFilters}
          onColumnFiltersChange={handleTableFiltersChange}
          globalFilter={debouncedSearch}
          emptyStateNode={
            <EmptyState
              icon={Users}
              title="No users found"
              description="Registered users appear here as they sign up."
            />
          }
        />
      </div>

      <DeleteConfirmation
        open={bulkDeleteOpen}
        onOpenChange={setBulkDeleteOpen}
        onConfirm={handleConfirmBulkDelete}
        title={`Delete ${selectedUserIds.length} user${selectedUserIds.length === 1 ? "" : "s"}?`}
        description={`Are you sure you want to delete ${selectedUserIds.length} user${selectedUserIds.length === 1 ? "" : "s"}? This action cannot be undone.`}
        entityType="user"
      />
    </div>
  );
}

import { createFileRoute } from '@tanstack/react-router';
export const Route = createFileRoute('/dashboard/settings/admin')({ component: SuperAdminPage });
